<script lang="ts">
    import Fa from 'svelte-fa'
    import { faPenNib, faCheck, faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons'
    import type { Board } from '../../../modules/pipelineApi/pipeline/boards/board'
    import type { BoardCreateDto } from '../../../modules/pipelineApi/pipeline/boards/boards-create.dto'
    import { BoardsRepository } from '../../../modules/pipelineApi/pipeline/boards/boards.repository'
    import { BoardsService } from '../../../modules/pipelineApi/pipeline/boards/boards.service'
    import type { BoardUpdateDto } from '../../../modules/pipelineApi/pipeline/boards/boards-update.dto'
    import ListItem from './list-item.svelte'
    import { List } from '../../../modules/pipelineApi/pipeline/lists/list'
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import { DragAndDrop, DragAndDropContainer } from '../dragAndDrop/dragAndDrop.js'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { ListsRepository } from '../../../modules/pipelineApi/pipeline/lists/lists.repository'
    import { ListsService } from '../../../modules/pipelineApi/pipeline/lists/lists.service'
    import type { MoveListDto } from '../../../modules/pipelineApi/pipeline/lists/lists-move.dto'
    const dispatch = createEventDispatcher()

    export let board: Board
    let className = ''
    export { className as class }

    let edit: boolean = false
    let show: boolean = false
    let allowNewItem: boolean = true

    const boardR: BoardsRepository = new BoardsRepository()
    const boardS: BoardsService = new BoardsService(boardR)
    let dd = null

    function editEntity() {
        edit = true
    }

    function saveEntity() {
        if (board.boardId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function createEntity() {
        try {
            if (board.label.trim().length === 0) {
                throw new Error('Informe o nome do pipeline')
            }
            show = true
            const dto: BoardCreateDto = {
                label: board.label,
            }
            let result = await boardS.create(dto)
            dispatch('change', { detail: result })
            edit = false
            allowNewItem = true
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            if (board.label.trim().length === 0) {
                throw new Error('Informe o nome do pipeline')
            }
            show = true
            const dto: BoardUpdateDto = {
                label: board.label,
            }
            let result = await boardS.update(board.boardId, dto)
            dispatch('change', { detail: result })
            edit = false
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
            allowNewItem = true
        }
    }

    async function removeEntity() {
        if (!board.boardId) {
            edit = false
            allowNewItem = true
            dispatch('change')
            return
        }
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await boardS.remove(board.boardId)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
            edit = false
        }
    }

    let emptyList: List
    let newList: boolean = false
    function newListItem() {
        emptyList = new List()
        emptyList.boardId = board.boardId
        emptyList.label = ''
        newList = true
    }

    function newListSuccess() {
        newList = false
        dispatch('change')
    }

    function evaluateError(error) {
        window.error(error)
    }

    async function listReordered(result) {
        console.log('ordering', result)

        const endpoint = 'boards/' + board.boardId + '/lists'
        const cardR: ListsRepository = new ListsRepository(endpoint)
        const cardS: ListsService = new ListsService(cardR)
        const dto: MoveListDto = {
            boardId: board.boardId,
            position: result.destination.index,
        }
        await cardS.move(result.element.data.id, dto)
    }

    async function startEdit() {
        dd.enabled = false
    }

    async function stopEdit() {
        dispatch('change')
        dd.enabled = true
    }

    let loaded = false
    let hide = false
    onMount(async () => {
        if (!board.boardId) {
            edit = true
            allowNewItem = false
        }
        loaded = true
        attachDragAndDrop()
    })

    let ddc = null
    async function attachDragAndDrop() {
        if (!loaded) return
        hide = true
        setTimeout(async () => {
            hide = false
            await tick()
            let dd = new DragAndDrop()
            dd.callback = (result) => {
                listReordered(result)
            }
            ;(ddc = new DragAndDropContainer('b_' + board.boardId, dd)), 100
        })
    }

    function onIntegration(ev) {
        dispatch('integration', ev.detail)
    }

    $: {
        if (board) {
            attachDragAndDrop()
        }
    }
</script>

<div class="root config-box {className}">
    <div class="input-group">
        <label for="" class="input-group-text">Nome do pipeline</label>
        {#if edit}
            <input class="form-control" bind:value={board.label} type="text" />
            <button class="btn btn-warning" on:click={removeEntity}><Fa icon={faTrashAlt} /></button>
            <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faCheck} /></button>
        {:else}
            <input class="form-control" disabled value={board.label + ' / ID: ' + board.boardId} type="text" />
            <button class="btn btn-primary" on:click={editEntity}><Fa icon={faPenNib} /></button>
        {/if}
    </div>
    {#if !hide}
        <div class="lists">
            <div data-id={board.boardId} class="draggable" id="b_{board.boardId}">
                {#each board.lists as list, i (list.listId)}
                    <div data-id={list.listId} class="listItem" id="item_{i}">
                        <ListItem on:integration={onIntegration} on:change={stopEdit} on:edit={startEdit} {list} />
                    </div>
                {/each}
            </div>
            <div class="listItem newList">
                {#if allowNewItem}
                    {#if newList}
                        <ListItem on:integration={onIntegration} list={emptyList} on:change={newListSuccess} />
                    {:else}
                        <button class="btn btn-secondary btn-sm" on:click={newListItem}><Fa icon={faPlus} /></button>
                    {/if}
                {/if}
            </div>
        </div>
    {/if}
</div>
<LoadSpinner {show} />

<style>
    .root {
        margin: 16px 0px;
    }
    input:disabled {
        background-color: rgba(var(--background-color), 0.8);
        color: rgb(var(--foreground-color));
        border: 1px solid #99999933;
    }
    .listItem {
        margin-top: 4px;
    }
    .newList {
        text-align: right;
    }
    .draggable {
        user-select: none;
    }
    .btn {
        min-width: 119px;
    }
</style>
